import React, { useEffect, useMemo, useState } from "react";
import { Group } from "@iventis/types";
import { IGridColours } from "@iventis/styles";
import { IGridContext, IGridContextImplementation } from "../state/interactive-grid-context";
import { IGridHeaderVertical } from "./interactive-grid-header-vertical.component";
import { IGridHeaderHorizontal } from "./interactive-grid-header-horizontal.component";
import { IGridInteractiveClipZone } from "./interactive-grid-clipzone.component";
import { IGridBody } from "./interactive-grid-body.component";
import { HeaderRenderer, ItemRenderer } from "../interactive-grid.types";
import type { IGridItem } from "../interactive-grid.types";

/**
 * Interactive Grid Component
 * Provides a convenient wrapper for all IGrid functionality
 * Can be used externally
 */
export const IGridComponent: React.FC<{
    xGroups: Group[];
    rowHeaderGroups: Group[];
    items: IGridItem[];
    rowHeadersWidth: number;
    columnHeadersHeight: number;
    columnWidth: number;
    renderFirstLevelRowHeader: HeaderRenderer;
    renderSecondLevelRowHeader: HeaderRenderer;
    renderThirdLevelRowHeader: HeaderRenderer;
    renderColumnHeader: HeaderRenderer;
    renderOriginHeader: (updateParentWidth: (width: { venue: number; discipline: number }) => void, parentWidth: { venue: number; discipline: number }) => React.ReactElement;
    renderItem: ItemRenderer;
    colours?: IGridColours;
}> = ({
    xGroups,
    rowHeaderGroups,
    items,
    rowHeadersWidth,
    columnHeadersHeight,
    columnWidth,
    renderColumnHeader,
    renderFirstLevelRowHeader,
    renderSecondLevelRowHeader,
    renderThirdLevelRowHeader,
    renderOriginHeader,
    renderItem,
    colours,
}) => {
    const gridContext = useMemo(() => new IGridContextImplementation(), []);
    const [parentWidth, setParentWidth] = useState({ venue: rowHeadersWidth, discipline: rowHeadersWidth });
    const [hasBeenResized, setHasBeenResized] = useState(false);
    const updateParentWidth = (width: { venue: number; discipline: number }) => {
        setHasBeenResized(true);
        setParentWidth(width);
    };

    useEffect(() => {
        gridContext.setRowHeaderGroups(rowHeaderGroups);
    }, [rowHeaderGroups]);

    if (colours) {
        gridContext.colours.next(colours);
    }

    const totalWidth = useMemo(() => xGroups.length * columnWidth + rowHeadersWidth, [rowHeadersWidth, columnWidth, xGroups.length]);

    return (
        <IGridContext.Provider value={gridContext}>
            <IGridInteractiveClipZone>
                <IGridBody
                    xGroups={xGroups}
                    items={items}
                    offsetX={parentWidth.venue + parentWidth.discipline}
                    offsetY={columnHeadersHeight}
                    groupColumnWidth={columnWidth}
                    renderItem={renderItem}
                />
                <IGridHeaderVertical
                    width={parentWidth}
                    offsetY={columnHeadersHeight}
                    totalWidth={totalWidth}
                    renderFirstLevelRowHeader={renderFirstLevelRowHeader}
                    renderSecondLevelRowHeader={renderSecondLevelRowHeader}
                    renderThirdLevelRowHeader={renderThirdLevelRowHeader}
                    hasBeenResized={hasBeenResized}
                    setParentWidth={updateParentWidth}
                />
                <IGridHeaderHorizontal
                    groups={xGroups}
                    height={columnHeadersHeight}
                    groupWidth={columnWidth}
                    originWidth={parentWidth.venue + parentWidth.discipline}
                    renderColumnHeader={renderColumnHeader}
                />
                <div style={{ position: "absolute", height: columnHeadersHeight }}>{renderOriginHeader(updateParentWidth, parentWidth)}</div>
            </IGridInteractiveClipZone>
        </IGridContext.Provider>
    );
};
